export const standardCupcakeFlavors = ['White Chocolate', 'Chocolate', 'German Chocolate'];

export const specialtyCupcakeFlavors = [
  'Marble', 'Red Velvet', 'Yellow', 'Amaretto', 'Butter Rum', 'Carrot', 'Coconut', 'Confetti', 'Orange Cream',
  'Italian Cream', 'Lemon', 'Spice', 'Strawberry',
];

/** Per specialty flavor or tint color: ≤4 dozen-equivalents → low fee; 5+ → high fee. */
const CUPCAKE_ADDON_TIER_DOZENS = 5;
const CUPCAKE_ADDON_FEE_LOW = 5;
const CUPCAKE_ADDON_FEE_HIGH = 10;

export const CUPCAKE_CONFIG_MODES = {
  UNIFORM: 'uniform',
  HALF_AND_HALF: 'half_and_half',
  BY_DOZEN: 'by_dozen',
};

export const CUPCAKE_CONFIG_MODE_OPTIONS = [
  {
    id: CUPCAKE_CONFIG_MODES.UNIFORM,
    title: 'All cupcakes same configuration',
    description: 'One flavor, icing, and sprinkle setup for your entire order.',
  },
  {
    id: CUPCAKE_CONFIG_MODES.HALF_AND_HALF,
    title: 'Half and half customization',
    description: 'Split your order in two — first half one way, second half another.',
  },
  {
    id: CUPCAKE_CONFIG_MODES.BY_DOZEN,
    title: 'Customize by dozen',
    description: 'Set flavors, icing, and sprinkles for each dozen individually.',
  },
];

export function getCupcakeConfigModeOptions(totalDozens) {
  const dozens = Number(totalDozens);
  if (dozens <= 1) {
    return [
      {
        id: CUPCAKE_CONFIG_MODES.UNIFORM,
        title: 'All cupcakes same configuration',
        description: 'One flavor, icing, and sprinkles for all 12 cupcakes.',
      },
      {
        id: CUPCAKE_CONFIG_MODES.HALF_AND_HALF,
        title: 'Half and half customization',
        description: 'Six cupcakes one flavor, six cupcakes another.',
      },
    ];
  }
  if (Math.abs(dozens - 2) < 1e-6 && !totalQuantityHasHalfDozen(dozens)) {
    return [
      {
        id: CUPCAKE_CONFIG_MODES.UNIFORM,
        title: 'All cupcakes same configuration',
        description: 'One setup for all 24 cupcakes (both dozens).',
      },
      {
        id: CUPCAKE_CONFIG_MODES.HALF_AND_HALF,
        title: 'Half and half customization',
        description: 'Configure each dozen separately — one flavor per dozen.',
      },
      CUPCAKE_CONFIG_MODE_OPTIONS[2],
    ];
  }
  return CUPCAKE_CONFIG_MODE_OPTIONS;
}

export function buildCupcakeUnitSlots(totalDozens) {
  const slots = [];
  let remaining = Number(totalDozens) || 0;
  let n = 1;

  while (remaining > 0.001) {
    const size = remaining >= 1 ? 1 : 0.5;
    slots.push({
      unitIndex: n,
      size,
      label: size === 1 ? `Dozen ${n}` : `Half dozen ${n}`,
    });
    remaining -= size;
    n += 1;
  }

  return slots;
}

/** True when total quantity ends in a half dozen (e.g. 3.5, 8.5). */
export function totalQuantityHasHalfDozen(totalDozens) {
  const total = Number(totalDozens) || 0;
  return Math.abs(total % 1 - 0.5) < 1e-6;
}

/**
 * Split sizes for multi-dozen half-and-half mode.
 * Whole dozens: equal halves (3 → 1.5 + 1.5, 8 → 4 + 4).
 * Half-dozen totals: lower whole + lower+0.5 (8.5 → 4 + 4.5, 3.5 → 1.5 + 2).
 */
export function getHalfAndHalfSplitSizes(totalDozens) {
  const total = Number(totalDozens) || 0;
  if (totalQuantityHasHalfDozen(total)) {
    const first = (total - 0.5) / 2;
    return { first, second: first + 0.5 };
  }
  const half = total / 2;
  return { first: half, second: half };
}

export function buildHalfAndHalfUnitSlots(totalDozens) {
  const total = Number(totalDozens) || 0;
  if (Math.abs(total - 2) < 1e-6 && !totalQuantityHasHalfDozen(total)) {
    return [
      { unitIndex: 1, size: 1, label: 'Dozen 1' },
      { unitIndex: 2, size: 1, label: 'Dozen 2' },
    ];
  }
  const { first, second } = getHalfAndHalfSplitSizes(totalDozens);
  return [
    { unitIndex: 1, size: first, label: `First half (${first} dozen)` },
    { unitIndex: 2, size: second, label: `Second half (${second} dozen)` },
  ];
}

export function createDefaultUnitConfig(slot) {
  return {
    unitIndex: slot.unitIndex,
    size: slot.size,
    label: slot.label,
    halfAndHalf: false,
    flavors: [],
    icingSelections: [],
    icingTintEnabled: false,
    icingTintColor: '',
    sugarSprinkleColors: [],
  };
}

export function pickUnitTemplateFields(config = {}) {
  return {
    halfAndHalf: Boolean(config.halfAndHalf),
    flavors: [...(config.flavors || [])],
    icingSelections: [...(config.icingSelections || [])],
    icingTintEnabled: Boolean(config.icingTintEnabled),
    icingTintColor: config.icingTintColor || '',
    sugarSprinkleColors: [...(config.sugarSprinkleColors || [])],
  };
}

export function applyTemplateToUnit(unit, template) {
  const fields = pickUnitTemplateFields(template);
  const halfAndHalf = fields.halfAndHalf;
  return {
    ...unit,
    ...fields,
    halfAndHalf,
    flavors: halfAndHalf ? fields.flavors.slice(0, 2) : fields.flavors.slice(0, 1),
  };
}

export function applyTemplateToUnits(units, template) {
  return (units || []).map((unit) => applyTemplateToUnit(unit, template));
}

export function splitUnitsIntoHalves(units) {
  const total = (units || []).reduce((sum, unit) => sum + unit.size, 0);
  const midpoint = total / 2;
  let cumulative = 0;
  const firstHalf = [];
  const secondHalf = [];

  for (const unit of units || []) {
    if (cumulative + unit.size <= midpoint + 1e-6) {
      firstHalf.push(unit);
      cumulative += unit.size;
      continue;
    }
    if (cumulative >= midpoint - 1e-6) {
      secondHalf.push(unit);
      continue;
    }

    const firstShare = midpoint - cumulative;
    const secondShare = unit.size - firstShare;
    if (firstShare >= secondShare) firstHalf.push(unit);
    else secondHalf.push(unit);
    cumulative += unit.size;
  }

  return { firstHalf, secondHalf };
}

/** Inner 6+6 split within one dozen — only when the whole order is exactly 1 dozen. */
export function halfBlockAllowsHalfAndHalf(units, totalDozens) {
  return orderAllowsHalfAndHalf(totalDozens);
}

export function syncCupcakeUnitConfigs(prevConfigs, totalDozens, configMode = CUPCAKE_CONFIG_MODES.UNIFORM) {
  const slots = buildCupcakeUnitSlots(totalDozens);
  const singleDozenOrder = orderAllowsHalfAndHalf(totalDozens);

  const synced = slots.map((slot) => {
    const existing = (prevConfigs || []).find((c) => c.unitIndex === slot.unitIndex);
    if (existing && existing.size === slot.size) {
      const halfAndHalf = singleDozenOrder ? existing.halfAndHalf : false;
      const flavors = halfAndHalf
        ? existing.flavors.slice(0, 2)
        : existing.flavors.slice(0, 1);

      return {
        ...existing,
        label: slot.label,
        halfAndHalf,
        flavors,
      };
    }
    return createDefaultUnitConfig(slot);
  });

  if (singleDozenOrder) {
    if (configMode === CUPCAKE_CONFIG_MODES.UNIFORM) {
      return synced.map((unit) => ({
        ...unit,
        halfAndHalf: false,
        flavors: unit.flavors.slice(0, 1),
      }));
    }
    if (configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF) {
      return synced.map((unit) => ({
        ...unit,
        halfAndHalf: true,
        flavors: unit.flavors.slice(0, 2),
      }));
    }
    return synced;
  }

  if (configMode === CUPCAKE_CONFIG_MODES.UNIFORM && synced[0]) {
    return applyTemplateToUnits(synced, synced[0]);
  }

  if (configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF) {
    const halfSlots = buildHalfAndHalfUnitSlots(totalDozens);
    const syncedHalves = halfSlots.map((slot) => {
      const existing = (prevConfigs || []).find(
        (c) => c.unitIndex === slot.unitIndex && Math.abs(Number(c.size) - slot.size) < 0.001
      );
      if (existing) {
        return {
          ...existing,
          label: slot.label,
          halfAndHalf: false,
          flavors: existing.flavors.slice(0, 1),
        };
      }
      return createDefaultUnitConfig(slot);
    });

    if ((prevConfigs || []).length > 2) {
      const { firstHalf, secondHalf } = splitUnitsIntoHalves(prevConfigs);
      return [
        applyTemplateToUnit(syncedHalves[0], firstHalf[0] || syncedHalves[0]),
        applyTemplateToUnit(syncedHalves[1], secondHalf[0] || firstHalf[0] || syncedHalves[1]),
      ];
    }

    if ((prevConfigs || []).length === 2) {
      return syncedHalves;
    }

    return syncedHalves;
  }

  return synced.map((unit) => ({
    ...unit,
    halfAndHalf: false,
    flavors: unit.flavors.slice(0, 1),
  }));
}

export function migrateLegacyCupcakeConfigs(formData) {
  const dozens = Number(formData.cupcakeDozens || 0);
  if (!dozens) return [];

  if (Array.isArray(formData.cupcakeUnitConfigs) && formData.cupcakeUnitConfigs.length > 0) {
    return syncCupcakeUnitConfigs(
      formData.cupcakeUnitConfigs,
      dozens,
      formData.cupcakeConfigMode || CUPCAKE_CONFIG_MODES.UNIFORM
    );
  }

  const slots = buildCupcakeUnitSlots(dozens);
  const legacyFlavors = formData.cupcakeFlavorSelections || [];
  const legacyIcing = formData.icingSelections || [];

  return slots.map((slot, idx) => ({
    ...createDefaultUnitConfig(slot),
    halfAndHalf: idx === 0 && legacyFlavors.length === 2,
    flavors: idx === 0 ? [...legacyFlavors] : [],
    icingSelections: idx === 0 ? [...legacyIcing] : [],
    icingTintEnabled: idx === 0 ? Boolean(formData.cupcakeIcingTintEnabled) : false,
    icingTintColor: idx === 0 ? formData.cupcakeIcingTintColor || '' : '',
    sugarSprinkleColors: idx === 0 ? [...(formData.sugarSprinkleColors || [])] : [],
  }));
}

export function isSingleDozenOrder(totalDozens, units) {
  const dozens = Number(totalDozens);
  return dozens === 1 && Array.isArray(units) && units.length === 1;
}

export function orderAllowsHalfAndHalf(totalDozens) {
  return Number(totalDozens) === 1;
}

/** Exactly 2 whole dozens (no trailing half-dozen on the order). */
export function isExactWholeTwoDozenOrder(totalDozens) {
  const total = Number(totalDozens) || 0;
  return Math.abs(total - 2) < 1e-6 && !totalQuantityHasHalfDozen(total);
}

export function usesPerDozenConfigurator(totalDozens, units) {
  return !isSingleDozenOrder(totalDozens, units);
}

export function formatCupcakeFrostingDisplay(icingSelections, tintEnabled, tintColor) {
  if (!icingSelections?.length) return '';
  return icingSelections
    .map((name) => {
      if (name === 'White Buttercream' && tintEnabled && tintColor && tintColor !== 'Black') {
        return `White Buttercream (tint: ${tintColor})`;
      }
      if (name === 'Chocolate Buttercream' && tintEnabled && tintColor === 'Black') {
        return 'Chocolate Buttercream (black — available only on chocolate buttercream)';
      }
      return name;
    })
    .join(', ');
}

export function formatUnitFlavorSummary(unit) {
  if (!unit.flavors?.length) return '—';
  if (unit.halfAndHalf && unit.flavors.length >= 2) {
    return `${unit.flavors[0]} / ${unit.flavors[1]} (half & half)`;
  }
  return unit.flavors.join(', ');
}

export function summarizeCupcakeFlavors(units) {
  if (!units?.length) return '';
  if (units.length === 1) return formatUnitFlavorSummary(units[0]);
  return units.map((u) => `${u.label}: ${formatUnitFlavorSummary(u)}`).join(' | ');
}

export function summarizeCupcakeIcing(units) {
  if (!units?.length) return '';
  if (units.length === 1) {
    return formatCupcakeFrostingDisplay(
      units[0].icingSelections,
      units[0].icingTintEnabled,
      units[0].icingTintColor
    );
  }
  return units
    .map((u) => `${u.label}: ${formatCupcakeFrostingDisplay(u.icingSelections, u.icingTintEnabled, u.icingTintColor)}`)
    .join(' | ');
}

export function summarizeCupcakeSprinkles(units) {
  if (!units?.length) return '';
  if (units.length === 1) {
    return (units[0].sugarSprinkleColors || []).join(' + ') || '—';
  }
  return units
    .map((u) => `${u.label}: ${(u.sugarSprinkleColors || []).join(' + ') || '—'}`)
    .join(' | ');
}

export function countSpecialtyFlavorsAcrossUnits(units) {
  const set = new Set();
  for (const unit of units || []) {
    for (const f of unit.flavors || []) {
      if (specialtyCupcakeFlavors.includes(f)) set.add(f);
    }
  }
  return set.size;
}

/** Flat fee for one specialty flavor or tint color from global dozen-equivalents using it. */
export function getCupcakeTieredAddonFee(dozenCount) {
  const dozens = Number(dozenCount) || 0;
  if (dozens <= 0) return 0;
  return dozens >= CUPCAKE_ADDON_TIER_DOZENS
    ? CUPCAKE_ADDON_FEE_HIGH
    : CUPCAKE_ADDON_FEE_LOW;
}

/** @deprecated alias — use getCupcakeTieredAddonFee */
export function getCupcakeSpecialtyFlavorFeePerFlavor(dozenCountForFlavor) {
  return getCupcakeTieredAddonFee(dozenCountForFlavor);
}

/** Maps each specialty flavor to total dozen-equivalents across the order. */
export function getSpecialtyFlavorDozenCounts(units) {
  const counts = new Map();

  for (const unit of units || []) {
    const size = Number(unit.size || 0);
    if (size <= 0) continue;

    const flavors = unit.flavors || [];
    const specialtyInUnit = flavors.filter((f) => specialtyCupcakeFlavors.includes(f));
    if (!specialtyInUnit.length) continue;

    if (unit.halfAndHalf && flavors.length >= 2) {
      const share = size / flavors.length;
      for (const f of specialtyInUnit) {
        counts.set(f, (counts.get(f) || 0) + share);
      }
    } else {
      for (const f of specialtyInUnit) {
        counts.set(f, (counts.get(f) || 0) + size);
      }
    }
  }

  return counts;
}

export function calcCupcakeSpecialtyFlavorPriceFromUnits(units) {
  const counts = getSpecialtyFlavorDozenCounts(units);
  let total = 0;
  for (const dozenCount of counts.values()) {
    total += getCupcakeTieredAddonFee(dozenCount);
  }
  return total;
}

/** Maps each tint color to total dozen-equivalents across the order (when tint is enabled). */
export function getTintColorDozenCounts(units) {
  const counts = new Map();

  for (const unit of units || []) {
    if (!unit.icingTintEnabled || !unit.icingTintColor) continue;
    const size = Number(unit.size || 0);
    if (size <= 0) continue;
    const color = unit.icingTintColor;
    counts.set(color, (counts.get(color) || 0) + size);
  }

  return counts;
}

export function calcCupcakeTintPriceFromUnits(units) {
  const counts = getTintColorDozenCounts(units);
  let total = 0;
  for (const dozenCount of counts.values()) {
    total += getCupcakeTieredAddonFee(dozenCount);
  }
  return total;
}

export function calcCupcakeAddonsPriceFromUnits(units) {
  return calcCupcakeSpecialtyFlavorPriceFromUnits(units) + calcCupcakeTintPriceFromUnits(units);
}

export function isUnitConfigured(unit) {
  const flavorOk = unit.halfAndHalf
    ? unit.flavors?.length === 2
    : (unit.flavors?.length || 0) >= 1;
  const icingOk = (unit.icingSelections?.length || 0) > 0;
  const tintOk = !unit.icingTintEnabled || Boolean(unit.icingTintColor);
  return flavorOk && icingOk && tintOk;
}

function validateUnitConfig(unit, label) {
  if (unit.halfAndHalf) {
    if (unit.flavors?.length !== 2) {
      return `${label}: select exactly two flavors for half & half.`;
    }
  } else if (!unit.flavors?.length) {
    return `${label}: select at least one flavor.`;
  } else if (unit.size === 0.5 && unit.flavors.length > 1) {
    return `${label}: half dozen allows one flavor only.`;
  }

  if (!unit.icingSelections?.length) {
    return `${label}: select at least one icing option.`;
  }
  if (unit.icingTintEnabled && !unit.icingTintColor) {
    return `${label}: select a buttercream tint color or turn off tinting.`;
  }

  return null;
}

export function validateCupcakeUnits(units, totalDozens, configMode = CUPCAKE_CONFIG_MODES.UNIFORM) {
  if (!units?.length) {
    return 'Please configure your cupcake order.';
  }

  if (isSingleDozenOrder(totalDozens, units)) {
    if (configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF) {
      return validateUnitConfig({ ...units[0], halfAndHalf: true }, 'Your dozen');
    }
    return validateUnitConfig({ ...units[0], halfAndHalf: false }, 'Your dozen');
  }

  if (configMode === CUPCAKE_CONFIG_MODES.UNIFORM) {
    return validateUnitConfig(units[0], 'All dozens');
  }

  if (configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF) {
    if (isExactWholeTwoDozenOrder(totalDozens) && units.length === 2) {
      for (const unit of units) {
        const err = validateUnitConfig(
          { ...unit, halfAndHalf: false },
          unit.label || `Dozen ${unit.unitIndex}`
        );
        if (err) return err;
      }
      return null;
    }

    const { firstHalf, secondHalf } = splitUnitsIntoHalves(units);
    const firstErr = validateUnitConfig(
      { ...(firstHalf[0] || {}), halfAndHalf: false },
      firstHalf[0]?.label || `First half (${firstHalf.reduce((s, u) => s + u.size, 0)} dozen)`
    );
    if (firstErr) return firstErr;
    return validateUnitConfig(
      { ...(secondHalf[0] || {}), halfAndHalf: false },
      secondHalf[0]?.label || `Second half (${secondHalf.reduce((s, u) => s + u.size, 0)} dozen)`
    );
  }

  for (const unit of units) {
    const err = validateUnitConfig(unit, unit.label);
    if (err) return err;
  }

  return null;
}
